/* eslint-disable no-param-reassign */
/* eslint-disable fp/no-mutation */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import { Notification } from 'src/v3/core/types'
import { dayDiffText } from 'src/v3/utilities/dates'

type BoundTreeStatus = 'subscribed' | 'limited' | 'unsubscribed'

type StripePrice = {
  amount: number
  priceID: string
  productName: string
}

type PromoResponse = {
  valid: boolean
  message?: string
  code?: string
  discount?: number
  discountType?: string
  originalAmount?: number
  discountedAmount?: number
  newSubscriptionDetails: {
    subscriptionId: string
    clientSecret?: string
  }
}

type SettingsSlice = {
  sessionStatus: Status
  featureFlagsEvaluted: boolean
  roles: string[]
  emailAddress: string
  linkedServices: string[]
  userID: string
  boundTreeStatus: BoundTreeStatus
  useScribeAccreditationAnalysis: boolean
  useEvents: boolean
  useSubscribePageV2: boolean
  useTranscriptEditor: boolean
  useCreateV4: boolean
  notifications: Notification[]
  notificationsStatus: Status
  checkoutPrice?: StripePrice
  checkoutPromoCode: string
  checkoutPromoResponse?: PromoResponse
  checkoutClientSecret: string
  checkoutPromoValidationStatus:
    | 'validating'
    | 'invalid'
    | 'valid'
    | 'error'
    | 'uninitialized'
}

export let initialState: SettingsSlice = {
  sessionStatus: 'uninitialized',
  featureFlagsEvaluted: false,
  roles: [],
  emailAddress: '',
  linkedServices: [],
  userID: '',
  boundTreeStatus: 'unsubscribed',
  useScribeAccreditationAnalysis: false,
  useEvents: false,
  useSubscribePageV2: false,
  useTranscriptEditor: false,
  useCreateV4: false,
  notifications: [],
  notificationsStatus: 'uninitialized',
  checkoutPromoCode: '',
  checkoutPromoValidationStatus: 'uninitialized',
  checkoutClientSecret: '',
}

export let fetchNotifications = createAsyncThunk(
  'settings/fetchNotifications',
  () =>
    axios
      .get<{ [kind: string]: Notification }[]>('/api/settings/notifications')
      .then(pluckData)
      .then((data) =>
        data.map((n): Notification => {
          let [kind, obj] = Object.entries(n)[0]
          return { ...obj, kind: kind as any }
        }),
      ),
)

export let settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    sessionStatusChanged: (state, action: PayloadAction<Status>) => {
      state.sessionStatus = action.payload
    },
    featureFlagsFlagEvaluated: (state) => {
      state.featureFlagsEvaluted = true
    },
    rolesChnaged: (state, action: PayloadAction<string[]>) => {
      state.roles = action.payload
    },
    emailAddressChanged: (state, action: PayloadAction<string>) => {
      state.emailAddress = action.payload
    },
    linkedServicesChanged: (state, action: PayloadAction<string[]>) => {
      state.linkedServices = action.payload
    },
    userIDChanged: (state, action: PayloadAction<string>) => {
      state.userID = action.payload
    },
    boundTreeStatusChanged: (state, action: PayloadAction<BoundTreeStatus>) => {
      state.boundTreeStatus = action.payload
    },
    useScribeAccreditationAnalysisChanged: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.useScribeAccreditationAnalysis = action.payload
    },
    useEventsChanged: (state, action: PayloadAction<boolean>) => {
      state.useEvents = action.payload
    },
    useSubscribePageV2Changed: (state, action: PayloadAction<boolean>) => {
      state.useSubscribePageV2 = action.payload
    },
    useTranscriptEditorChanged: (state, action: PayloadAction<boolean>) => {
      state.useTranscriptEditor = action.payload
    },
    useCreateV4Changed: (state, action: PayloadAction<boolean>) => {
      state.useCreateV4 = action.payload
    },
    checkoutPriceChanged: (state, action: PayloadAction<StripePrice>) => {
      state.checkoutPrice = action.payload
    },
    checkoutPromoCodeChanged: (state, action: PayloadAction<string>) => {
      state.checkoutPromoCode = action.payload
    },
    checkoutPromoValidationStatusChanged: (
      state,
      action: PayloadAction<SettingsSlice['checkoutPromoValidationStatus']>,
    ) => {
      state.checkoutPromoValidationStatus = action.payload
    },
    checkoutPromoResponseChanged: (
      state,
      action: PayloadAction<PromoResponse>,
    ) => {
      state.checkoutPromoResponse = action.payload
    },
    checkoutClientSecretChanged: (state, action: PayloadAction<string>) => {
      state.checkoutClientSecret = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.notificationsStatus = 'pending'
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.notificationsStatus = 'rejected'
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notificationsStatus = 'fulfilled'
        state.notifications = action.payload
      })
  },
})

export let {
  sessionStatusChanged,
  featureFlagsFlagEvaluated,
  rolesChnaged,
  emailAddressChanged,
  linkedServicesChanged,
  userIDChanged,
  boundTreeStatusChanged,
  useScribeAccreditationAnalysisChanged,
  useEventsChanged,
  useSubscribePageV2Changed,
  useTranscriptEditorChanged,
  useCreateV4Changed,
  checkoutPriceChanged,
  checkoutPromoCodeChanged,
  checkoutPromoValidationStatusChanged,
  checkoutPromoResponseChanged,
  checkoutClientSecretChanged,
} = settingsSlice.actions

export let selectSessionStatus = (state: AppRootState) =>
  state.settings.sessionStatus
export let selectFeatureFlagsEvaluated = (state: AppRootState) =>
  state.settings.featureFlagsEvaluted
export let selectRoles = (state: AppRootState) => state.settings.roles
export let selectEmailAddress = (state: AppRootState) =>
  state.settings.emailAddress
export let selectLinkedServices = (state: AppRootState) =>
  state.settings.linkedServices
export let selectUserID = (state: AppRootState) => state.settings.userID
export let selectBoundTreeStatus = (state: AppRootState) =>
  state.settings.boundTreeStatus

export let selectUseScribeAccreditationAnalysis = (state: AppRootState) =>
  state.settings.useScribeAccreditationAnalysis
export let selectUseEvents = (state: AppRootState) => state.settings.useEvents
export let selectUseSubscribePageV2 = (state: AppRootState) =>
  state.settings.useSubscribePageV2
export let selectUseTranscriptEditor = (state: AppRootState) =>
  state.settings.useTranscriptEditor
export let selectUseCreateV4 = (state: AppRootState) =>
  state.settings.useCreateV4

export let selectNotifications = (state: AppRootState) =>
  state.settings.notifications.reduce(
    (reduced, n) => {
      let message = ''
      let route = ''
      if (n.kind === 'assignmentAssigned') {
        message = `You have a new assignment: ${n.name}`
        route = '/profile/assignments'
      } else if (n.kind === 'assignmentDue') {
        let daysDiff = dayjs(n.dueDate).diff(dayjs(), 'days')
        let dayPrefix = daysDiff < 0 ? 'was' : 'is'
        message = `Your assignment ${n.name} ${dayPrefix} due ${dayDiffText(daysDiff)}.`
        route = '/profile/assignments'
      } else if (n.kind === 'credentialExpiring') {
        let daysDiff = dayjs(n.expirationDate).diff(dayjs(), 'days')
        let dayPrefix = daysDiff < 0 ? 'expired' : 'expires'
        message = `Your ${n.type} credential ${n.credentialNumber} ${dayPrefix} ${dayDiffText(daysDiff)}.`
        route = '/profile/credentials'
      }
      return [
        ...reduced,
        {
          id: n.id,
          status: n.status,
          message,
          route,
        },
      ]
    },
    [] as (Pick<Notification, 'id' | 'status'> & {
      message: string
      route: string
    })[],
  )
export let selectNoficationsStatus = (state: AppRootState) =>
  state.settings.notificationsStatus

export let selectCheckoutPrice = (state: AppRootState) =>
  state.settings.checkoutPrice
export let selectCheckoutPromoCode = (state: AppRootState) =>
  state.settings.checkoutPromoCode
export let selectCheckoutPromoValidationStatus = (state: AppRootState) =>
  state.settings.checkoutPromoValidationStatus
export let selectCheckoutPromoResponse = (state: AppRootState) =>
  state.settings.checkoutPromoResponse
export let selectCheckoutClientSecret = (state: AppRootState) =>
  state.settings.checkoutClientSecret

export default settingsSlice.reducer
